import { useStore } from '../store/store';

/**
 * Check if a transform name is unique within the current pipeline
 * @param name - The name to check for uniqueness
 * @param currentNodeId - The ID of the current node (excluded from check)
 * @returns true if the name is unique, false if it conflicts
 */
export function isTransformNameUnique(name: string, currentNodeId?: string): boolean {
  if (!name || name.trim() === '') {
    return true; // Empty names will be caught by required validation
  }

  const state = useStore.getState();
  const activePipeline = state.pipelines.find(p => p.id === state.activePipelineId);
  
  if (!activePipeline) {
    return true; // No active pipeline, name is unique
  }

  const normalizedName = name.trim().toLowerCase();
  
  // Check against all nodes except the current one
  const conflictingNode = activePipeline.nodes.find(node => 
    node.id !== currentNodeId && 
    node.data.name && 
    node.data.name.trim().toLowerCase() === normalizedName
  );

  return !conflictingNode;
}

/**
 * Get all existing transform names in the current pipeline (for reference)
 * @param excludeNodeId - Optional node ID to exclude from the list
 * @returns Array of existing transform names
 */
export function getExistingTransformNames(excludeNodeId?: string): string[] {
  const state = useStore.getState();
  const activePipeline = state.pipelines.find(p => p.id === state.activePipelineId);
  
  if (!activePipeline) {
    return [];
  }

  return activePipeline.nodes
    .filter(node => node.id !== excludeNodeId && node.data.name)
    .map(node => node.data.name)
    .filter(name => name && name.trim() !== '');
}

/**
 * Generate a unique transform name suggestion based on existing names
 * @param baseName - The base name to make unique
 * @param currentNodeId - The ID of the current node (excluded from check)
 * @returns A unique name suggestion
 */
export function generateUniqueTransformNameSuggestion(baseName: string, currentNodeId?: string): string {
  if (isTransformNameUnique(baseName, currentNodeId)) {
    return baseName;
  }

  const existingNames = getExistingTransformNames(currentNodeId);
  const baseNameLower = baseName.toLowerCase();
  
  // Find the highest number used with this base name
  let highestNumber = 1;
  existingNames.forEach(name => {
    const nameLower = name.toLowerCase();
    if (nameLower === baseNameLower) {
      highestNumber = Math.max(highestNumber, 1);
    } else if (nameLower.startsWith(baseNameLower + ' ')) {
      const suffix = nameLower.substring(baseNameLower.length + 1);
      const number = parseInt(suffix, 10);
      if (!isNaN(number) && number > highestNumber) {
        highestNumber = number;
      }
    }
  });

  return `${baseName} ${highestNumber + 1}`;
} 